import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import useGlobalEntitiesTable from "../../global-entities/useGlobalEntitiesTable";
import NoEntityAvailable from "../../global-entities/NoEntityAvailable";
import { downloadCSV } from "../../../utils/CSVFunctions";
import useDateTime from "~/hooks/useDateTime";

const AppointmentHistory = ({ historyData }) => {
  const { onPageChange } = useGlobalEntitiesTable();
  const { formatDateTime } = useDateTime();

  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState(historyData); // Store filtered data separately

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  function formatDate(dateString) {
    const date = new Date(dateString);
    const day = date.getDate();
    const suffix = ["th", "st", "nd", "rd"][
      day % 10 > 3 || Math.floor((day % 100) / 10) === 1 ? 0 : day % 10
    ];
    return (
      date.toLocaleString("en-US", { month: "short" }) +
      ` ${day}${suffix}, ${date.getFullYear()}`
    );
  }

  const statusTemplate = (rowData) => {
    switch (rowData?.status) {
      case "Active":
        return (
          <>
            <span className="pi pi-circle-fill active-circle-fill"></span>{" "}
            Active
          </>
        );
      case "Not Active":
        return (
          <>
            <span className="pi pi-circle-fill not-active-circle-fill"></span>{" "}
            Not Active
          </>
        );
      case "Removed":
        return (
          <>
            <span className="pi pi-circle-fill removed-circle-fill"></span>{" "}
            Removed
          </>
        );
      default:
        return null;
    }
  };

  const applyFilters = () => {
    let filtered = historyData;

    if (searchQuery) {
      filtered = historyData.filter((item) => {
        return (
          item?.position?.toLowerCase().includes(searchQuery) ||
          item?.start_date?.toLowerCase().includes(searchQuery) ||
          item?.status?.toLowerCase().includes(searchQuery) ||
          item?.end_date?.toLowerCase().includes(searchQuery)
        );
      });
    }

    setFilteredData(filtered);
  };

  const handleDownloadCSV = () => {
    if (filteredData.length === 0) {
      console.warn("No data to download");
      return;
    }
    downloadCSV(filteredData, "appointment_history.csv");
  };

  useEffect(() => {
    applyFilters();
  }, [searchQuery, historyData]);

  return (
    <div className="formgrid justify-content-between">
      <div className="flex gap-2 align-items-center mb-4 pl-0">
        <div className="input-wrapper-container">
          <span className="pi pi-search"></span>
          <input
            type="text"
            placeholder="Search appointment by role name, date or status"
            className="appearance-none outline-none w-full"
            value={searchQuery}
            onChange={handleSearchChange}
          />
          <span className="pi pi-search"></span>
        </div>
        <div className="btn-container">
          <button
            className="btn download-csv"
            onClick={() => handleDownloadCSV()}
          >
            <span>Download as CSV</span>
            <i className="pi pi-download ml-2"></i>
          </button>
        </div>
      </div>

      <Card className="p-5 w-full">
        <div className="field p-2 col-12 p-0 m-0 table-section">
          <div className="card">
            {filteredData?.length > 0 ? (
              <DataTable
                value={filteredData}
                sortMode="multiple"
                tableStyle={{ minWidth: "50rem" }}
                className="datatable-right-align"
                emptyMessage="No results were found"
                onPage={onPageChange}
              >
                <Column
                  field="position"
                  header="Positions"
                  className="table-col-width"
                />
                <Column
                  field="start_date"
                  header="Appointed"
                  className="table-col-width"
                  body={(rowData) => {
                    if (rowData?.start_date) {
                      return formatDateTime({
                        date: rowData?.start_date,
                        dateformat: "MMMM do, yyyy",
                      });
                    } else {
                      return "-";
                    }
                  }}
                />
                <Column
                  field="end_date"
                  header="End Date"
                  className="table-col-width"
                  body={(rowData) => {
                    if (rowData?.end_date) {
                      return formatDateTime({
                        date: rowData?.end_date,
                        dateformat: "MMMM do, yyyy",
                      });
                    } else {
                      return "-";
                    }
                  }}
                />
                <Column
                  field="status"
                  header="Status"
                  className="table-col-width"
                  body={statusTemplate}
                />
              </DataTable>
            ) : (
              <NoEntityAvailable message="No rows available..." />
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AppointmentHistory;
